import React, { useCallback, useEffect, useMemo, useState } from "react";
import { statusStyle } from "../studentStyles";
import {
  normalizeStudentScheduleType,
  normalizeStudentStatus,
} from "../../../../utils/studentLifecycle";
import { STUDENT_TABS, getTab, normalizeTabKey } from "./tabs/registry";
import { resolvePermissions } from "./permissions";
import "./StudentDashboard.css";

const AUTOSAVE_DELAY_MS = 900;

function normalizeStudent(student) {
  const fallbackLegacyNote = [student?.schoolLogin, student?.loginNote]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join("\n\n");

  return {
    ...(student || {}),
    name: student?.name || "",
    grade: student?.grade ?? "",
    status: normalizeStudentStatus(student?.status),
    note: student?.note ?? fallbackLegacyNote,
    subjects: Array.isArray(student?.subjects) ? student.subjects : [],
    schoolGrades: Array.isArray(student?.schoolGrades) ? student.schoolGrades : [],
    schoolCoursesTable: Array.isArray(student?.schoolCoursesTable) ? student.schoolCoursesTable : [],
    schoolGradeSnapshots: Array.isArray(student?.schoolGradeSnapshots) ? student.schoolGradeSnapshots : [],
    testScores: Array.isArray(student?.testScores) ? student.testScores : [],
    highSchoolCourses: Array.isArray(student?.highSchoolCourses) ? student.highSchoolCourses : [],
    conferences: Array.isArray(student?.conferences) ? student.conferences : [],
    customFields: student?.customFields && typeof student.customFields === "object" ? student.customFields : {},
    schoolPlan: student?.schoolPlan || "",
    schoolPortalUrl: student?.schoolPortalUrl || "",
    schoolLoginUsername: student?.schoolLoginUsername || "",
    schoolLoginPassword: student?.schoolLoginPassword || "",
    schoolLogin: student?.schoolLogin || "",
    loginNote: student?.loginNote || "",
    scheduleType: normalizeStudentScheduleType(student?.scheduleType, student?.status),
    scheduleDate: student?.scheduleDate || "",
    scheduleTime: student?.scheduleTime || "",
    diagnosticMatchName: student?.diagnosticMatchName || "",
    diagnosticUid: student?.diagnosticUid || "",
    diagnosticLinkNote: student?.diagnosticLinkNote || "",
  };
}

function normalizeStatusValue(status) {
  return normalizeStudentStatus(status);
}

function normalizeGradeValue(grade) {
  if (grade == null || String(grade).trim() === "") return "";
  const numeric = Number(grade);
  return Number.isFinite(numeric) ? numeric : grade;
}

function formatTotalHoursLabel(totalSessionHours) {
  const parsed = Number(totalSessionHours);
  if (!Number.isFinite(parsed) || parsed <= 0) return "0.0";
  return parsed.toFixed(1);
}

function sanitizeForSave(data) {
  const source = data && typeof data === "object" ? data : {};
  const { id, createdAt, updatedAt, ...rest } = source;
  return {
    ...rest,
    grade: normalizeGradeValue(rest.grade),
    status: normalizeStatusValue(rest.status),
    scheduleType: normalizeStudentScheduleType(rest.scheduleType, rest.status),
    scheduleDate: String(rest.scheduleDate || ""),
    scheduleTime: String(rest.scheduleTime || ""),
  };
}

function normalizeForCompare(value) {
  if (Array.isArray(value)) return value.map(normalizeForCompare);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = normalizeForCompare(value[key]);
        return acc;
      }, {});
  }
  return value;
}

function buildSnapshot(data) {
  return JSON.stringify(normalizeForCompare(sanitizeForSave(data)));
}

export default function StudentDashboard({
  student,
  onSaveStudent,
  activeTabKey,
  onRequestTabChange,
  permissions,
  onBack,
}) {
  const perms = useMemo(() => resolvePermissions(permissions), [permissions]);
  const [activeTab, setActiveTab] = useState(() => normalizeTabKey(activeTabKey));
  const [formData, setFormData] = useState(() => normalizeStudent(student));
  const [savedSnapshot, setSavedSnapshot] = useState(() => buildSnapshot(normalizeStudent(student)));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const normalized = normalizeStudent(student);
    setFormData(normalized);
    setSavedSnapshot(buildSnapshot(normalized));
  }, [student]);

  // Initialize / reset the active tab when the student changes or when an
  // initial tab is requested via navigation (e.g. "Conference Management").
  // After that, tab switching is owned entirely by local state — the URL
  // never changes per tab, keeping the profile one unified workspace.
  useEffect(() => {
    setActiveTab(normalizeTabKey(activeTabKey));
  }, [student?.id, activeTabKey]);

  const status = useMemo(() => normalizeStatusValue(formData.status), [formData.status]);
  const currentSnapshot = useMemo(() => buildSnapshot(formData), [formData]);
  const hasUnsavedChanges = currentSnapshot !== savedSnapshot;
  const activeTabConfig = useMemo(() => getTab(activeTab), [activeTab]);
  const autoSaveEnabled = !!activeTabConfig.autoSave;

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = useCallback(async (overrideData, { silent = false } = {}) => {
    if (!student?.id || saving) return;
    setSaving(true);
    try {
      const dataToSave = overrideData || formData;
      const payload = sanitizeForSave(dataToSave);
      await onSaveStudent(student.id, payload, { silent });
      setSavedSnapshot(buildSnapshot(payload));
      return true;
    } catch (error) {
      return false;
    } finally {
      setSaving(false);
    }
  }, [student?.id, saving, formData, onSaveStudent]);

  // ── Debounced autosave for tabs that edit the shared student form ──
  useEffect(() => {
    if (!autoSaveEnabled || !hasUnsavedChanges || !student?.id || saving) return undefined;
    const timer = setTimeout(() => {
      handleSave(undefined, { silent: true });
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [autoSaveEnabled, hasUnsavedChanges, currentSnapshot, saving, student?.id, handleSave]);

  const confirmNavigationWithUnsavedChanges = useCallback(async () => {
    if (!hasUnsavedChanges) return true;

    const shouldSave = window.confirm(
      "You have unsaved changes.\n\nClick OK to save and continue.\nClick Cancel for more options."
    );
    if (shouldSave) {
      return !!(await handleSave());
    }

    const shouldDiscard = window.confirm(
      "Discard your unsaved changes and continue?\n\nClick OK to discard, or Cancel to stay here."
    );
    if (!shouldDiscard) return false;

    setFormData(normalizeStudent(student));
    return true;
  }, [hasUnsavedChanges, handleSave, student]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleTabChange = async (tabKey) => {
    const nextTab = normalizeTabKey(tabKey);
    if (nextTab === activeTab) return;

    if (hasUnsavedChanges) {
      if (autoSaveEnabled) {
        // Flush pending edits silently before leaving an autosave tab.
        await handleSave(undefined, { silent: true });
      } else {
        const canContinue = await confirmNavigationWithUnsavedChanges();
        if (!canContinue) return;
      }
    }

    if (typeof onRequestTabChange === "function") {
      onRequestTabChange(nextTab);
      return;
    }
    setActiveTab(nextTab);
  };

  const ActiveComponent = activeTabConfig.component;
  const tabProps = {
    student: formData,
    setField,
    setFormData,
    onSaveTab: handleSave,
    saving,
    permissions: perms,
  };

  return (
    <div className="sd-dash">
      <div className="sd-dash__bar">
        <header className="sd-dash__header">
          {typeof onBack === "function" && (
            <button type="button" className="sd-dash__back" onClick={onBack}>
              <span aria-hidden="true">←</span> Back
            </button>
          )}
          <h1 className="sd-dash__name" title={formData.name || "Student"}>
            {formData.name || "Student"}
          </h1>
          <div className="sd-dash__meta">
            <span className="sd-dash__status" style={statusStyle(status)}>{status}</span>
            <span className="sd-dash__dot" aria-hidden="true" />
            <span className="sd-dash__chip">Grade {formData.grade || "—"}</span>
            <span className="sd-dash__dot" aria-hidden="true" />
            <span className="sd-dash__chip">{formatTotalHoursLabel(formData.totalSessionHours)} hrs</span>
            {autoSaveEnabled && saving && <span className="sd-dash__saving">Saving…</span>}
          </div>
        </header>

        <nav className="sd-dash__tabs" aria-label="Student sections">
          {STUDENT_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`sd-dash__tab ${activeTab === tab.key ? "is-active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
              aria-current={activeTab === tab.key ? "page" : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <main className="sd-dash__content" key={activeTab}>
        <ActiveComponent {...tabProps} />
      </main>
    </div>
  );
}
